// ============================================================================
// Sending a sub their application (item 869, Ben 2026-10-14).
// Two ways, the account manager's pick:
//   • Open in my text app — Messages opens with the number and the link, as before.
//   • I'll send it myself — the name, the phone and the link, each with Copy,
//     so the link can go by any app: text, WhatsApp, Facebook, email.
// Used by + Add (a new Lead) and by the profile's "Send the application".
// Nothing is sent by the app; the link is the sub's own (applicationLinkFor).
// ============================================================================

import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useToast } from '../../../shared/components';
import { openText } from './actions';
import { bigPrimary, bigSecondary } from './sheet';

export interface SendApplicationTarget {
  name: string;
  phone: string | null;
  link: string;
}

/** The text the "Open in my text app" button puts in Messages — unchanged from before. */
export function applicationMessage(name: string, link: string): string {
  return `Hi ${name}, this is your Terminal Cleaning Services application: ${link}`;
}

export function SendApplicationChoice({ target, onDone }: { target: SendApplicationTarget; onDone: () => void }) {
  const [mode, setMode] = useState<'choose' | 'copy'>('choose');

  if (mode === 'choose') {
    return (
      <div>
        <p className="text-sm text-tcs-text-secondary">How do you want to send {target.name} the application?</p>
        <button
          type="button"
          className={bigPrimary}
          onClick={() => {
            openText(target.phone, applicationMessage(target.name, target.link));
            onDone();
          }}
        >
          Open in my text app
        </button>
        <button type="button" className={bigSecondary} onClick={() => setMode('copy')}>
          I'll send it myself — copy and paste
        </button>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-2 text-sm text-tcs-text-secondary">Copy each one into any app — text, WhatsApp, Facebook, email.</p>
      <CopyRow label="Name" value={target.name} />
      <CopyRow label="Phone" value={target.phone ?? ''} />
      <CopyRow label="Application link" value={target.link} />
      <button type="button" className={bigPrimary} onClick={onDone}>
        Done
      </button>
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — let the person copy it by hand (the CopyReference fallback).
      window.prompt(`Copy the ${label.toLowerCase()}:`, value);
    }
  }
  return (
    <div className="mb-2">
      <div className="mb-0.5 text-xs text-tcs-text-muted">{label}</div>
      <div className="flex items-stretch gap-2">
        <div className="min-w-0 flex-1 break-all rounded-lg border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text">
          {value || '—'}
        </div>
        <button
          type="button"
          aria-label={`Copy the ${label.toLowerCase()}`}
          disabled={!value}
          onClick={() => void copy()}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-tcs-border bg-tcs-raised px-3 text-sm font-medium text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-40"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
